import { useState, type ReactNode } from "react";

interface Props {
  title: string;
  // Явный `| undefined`: включён exactOptionalPropertyTypes, при котором
  // «свойство можно не передавать» и «свойству можно передать undefined» —
  // разные вещи, а вызывающий код передаёт именно undefined.
  subtitle?: string | undefined;
  actions?: ReactNode | undefined;
  /**
   * Классы КОНТЕЙНЕРА, а не содержимого. Нужны там, где карточка должна
   * тянуться по высоте соседней колонки (`h-full`): растянуть её снаружи
   * нельзя — обёртка не передаёт высоту секции внутри.
   */
  className?: string | undefined;
  /**
   * Плотная карточка: шапка и тело с уменьшенными отступами.
   *
   * Флаг, а не новое умолчание. Тесно ровно на одной странице из шести и в
   * четырёх карточках из двадцати пяти; на кошельке и сигналах воздух между
   * панелями как раз и делает десяток карточек просматриваемым. Глобальное
   * уплотнение — правка на весь продукт ради дефекта одного экрана, и откатить
   * её пришлось бы целиком.
   *
   * Булев, а не `headerClassName`/`bodyClassName`: escape-hatch классами делает
   * плотность решением каждого вызывающего, и через три правки на экране будет
   * три разных отступа.
   */
  dense?: boolean | undefined;
  /**
   * Тело скрыто, но СМОНТИРОВАНО.
   *
   * Именно скрыто, а не выброшено: у свёрнутой карточки может стоять набранное
   * оператором (сумма в форме ордера), и размонтирование потеряло бы его молча.
   * Отступы тела при этом не считаются — `display: none` убирает их вместе с
   * содержимым, а без этого свёрнутая карточка оставляла бы пустую полосу.
   */
  collapsed?: boolean | undefined;
  /**
   * Пометка у заголовка: «устарело», «новое» и тому подобное.
   *
   * Отдельный слот, а не приписка к subtitle: подзаголовок несёт ИЗМЕРЕНИЕ
   * («тейкером 61 из 733 окон»), и слово о статусе раздела, поставленное туда
   * же, читалось бы как часть результата.
   */
  badge?: string | undefined;
  children: ReactNode;
}

export function Panel({
  title,
  subtitle,
  actions,
  className,
  dense,
  collapsed,
  badge,
  children,
}: Props) {
  return (
    <section
      className={`flex flex-col rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)]${
        className ? ` ${className}` : ""
      }`}
    >
      {/* flex-wrap и min-w-0 — не про плотность, а про переполнение: слот
          действий ничем не ограничен, и панель с широким слотом (набор сумм,
          пороги, переключатели) сжимала бы заголовок вместо переноса. */}
      <div
        className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-[var(--color-border)] px-4 ${
          dense ? "py-1.5" : "py-2.5"
        }`}
      >
        <div className="flex min-w-0 items-baseline gap-2">
          <h2 className="text-sm font-medium">{title}</h2>
          {badge && (
            <span className="shrink-0 rounded border border-[var(--color-border)] px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-[var(--color-muted)]">
              {badge}
            </span>
          )}
          {subtitle && (
            <span className="min-w-0 truncate text-xs text-[var(--color-muted)]">{subtitle}</span>
          )}
        </div>
        {actions}
      </div>
      {/* flex-1: при h-full у карточки тянуться должно ТЕЛО, иначе содержимое
          осталось бы прижатым к шапке, а рамка ушла вниз пустой. */}
      <div className={`${collapsed ? "hidden" : "flex-1"} ${dense ? "p-3" : "p-4"}`}>
        {children}
      </div>
    </section>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return (
    <p className="py-6 text-center text-sm text-[var(--color-muted)]">{children}</p>
  );
}

/**
 * Свёрнутая подсказка «как читать» — прозой под таблицей.
 *
 * Текст под сводками объясняет, ЧТО значат колонки и чего по ним утверждать
 * нельзя: знаменатели разные, зелёное — от двух сигм, ордер по строке не
 * ставится сам собой. Выбросить его нельзя — без него таблицу читают неверно, —
 * но и держать раскрытым не за что: правило читают один раз, а числа под ним
 * смотрят каждый день. Развёрнутые подсказки на экране сигналов занимали больше
 * места, чем все шесть сводок вместе, и сводки переставали сравниваться глазами
 * ровно по тому же доводу, по которому под ними свёрнуты ленты.
 *
 * Нативный `<details>`, а не Panel с состоянием: подсказка — статический текст,
 * ни хуков, ни запросов внутри нет, и прятать её ради сети не нужно. Заодно
 * работает поиск по странице — браузер раскрывает найденное в свёрнутом
 * `<details>` сам.
 */
export function Note({
  /**
   * Что обещает раскрытие.
   *
   * Узел, а не строка: под свёрнутым списком приговоров прячется вывод, и
   * подпись обязана его вынести — «ступени набраны на РАЗНЫХ окнах» цветом
   * предупреждения читается со свёрнутого экрана, а внутри лежит только
   * разбор по строкам. Умолчание годится там, где под фолдом одно объяснение.
   */
  label,
  children,
}: {
  label?: ReactNode | undefined;
  children: ReactNode;
}) {
  return (
    <details className="mt-3">
      <summary className="select-none text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]">
        {label ?? "как читать"}
      </summary>
      <div className="mt-2 text-xs leading-relaxed text-[var(--color-muted)]">{children}</div>
    </details>
  );
}

/**
 * Панель, которую можно свернуть, — и свёрнутая она НЕ ХОДИТ В СЕТЬ.
 *
 * # Чем отличается от Panel collapsed
 *
 * Ровно одним, и различие принципиальное. `collapsed` ПРЯЧЕТ смонтированное
 * тело: у свёрнутой карточки может стоять набранное оператором, и потерять его
 * нельзя. Здесь тело РАЗМОНТИРОВАНО, пока карточку не открыли, — и именно
 * поэтому запросы из него не уходят: хук, который не смонтирован, не заводит ни
 * опроса, ни таймера.
 *
 * Это и есть смысл компонента. Ленты теневых журналов опрашивают сервер раз в
 * пятнадцать секунд каждая, и на странице их четыре; страница же интересна
 * результатами, а не логом входов, и в свёрнутом виде она обязана молчать, а не
 * просто прятать таблицу. Отсюда правило: под FoldablePanel кладут КОМПОНЕНТ, а
 * не готовую разметку — JSX-элемент, который не отрисован, хуков не вызывает.
 *
 * Состояние живёт в самой карточке и не переживает перезагрузку. Так и надо:
 * умолчание — часть ответа на вопрос «что показывать первым», и хранить его на
 * стороне оператора значило бы, что новая вкладка показывает не то, что решено
 * кодом.
 */
export function FoldablePanel({
  title,
  subtitle,
  badge,
  dense,
  defaultOpen,
  children,
}: {
  title: string;
  subtitle?: string | undefined;
  badge?: string | undefined;
  dense?: boolean | undefined;
  /** Открыта ли при первом показе. Умолчание — свёрнута. */
  defaultOpen?: boolean | undefined;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <Panel
      title={title}
      subtitle={subtitle}
      badge={badge}
      dense={dense}
      collapsed={!open}
      actions={
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="rounded border border-[var(--color-border)] px-2 py-0.5 text-xs text-[var(--color-muted)] hover:text-[var(--color-fg)]"
          aria-expanded={open}
        >
          {open ? "свернуть" : "показать"}
        </button>
      }
    >
      {/* Тело появляется ТОЛЬКО открытым: пока его нет в дереве, нет и запроса.
          Спрятать его через collapsed было бы недостаточно — скрытый хук
          продолжает опрашивать сервер каждые пятнадцать секунд. */}
      {open ? children : null}
    </Panel>
  );
}
